import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sendCustomTrackingInfo } from '@panel/commons';
import styled from 'styled-components';
import Cookies from 'js-cookie';
import { getDevice, execCookiesDL, getPublisherTypeNameDL, getPublisherScoreValue } from '@panel/commons/helpers';
import { useTranslation } from 'react-i18next';
import { userInfoSelectors } from '@panel/core/src/state/ducks/user';
import SnackBarContainer from '@panel/commons/lib/SimpleSnackbar/SnackBarContainer';
import { ScoreSelectors } from '@panel/estadisticas/src/ducks/score';
import usePostingSavedSnackbar from '@panel/core/src/hooks/usePostingSavedSnackbar';
import { FilterSelectors } from '../../../ducks/filter';
import { PostingsOperations, PostingsSelectors } from '../../../ducks/posting';
import PostingsListDesktop from '../PostingsListDesktop/PostingsListDesktop';
import { ReservationSelectors } from '../../../ducks/reservation';
import { CancelReservationSelectors } from '../../../ducks/cancelReservation';
import PostingsListMobile from '../PostingsListMobile/PostingsListMobile';
import { SuspendSelectors, SuspendOperations } from '../../../ducks/suspend';
import { PublishSelectors, PublishOperations } from '../../../ducks/publish';
import { ArchiveSelectors, ArchiveOperations } from '../../../ducks/archive';
import { ResponsibleUpdateSelectors } from '../../../ducks/responsibleUpdate';
import { creditsOperations } from '../../../ducks/credits';
import TourContainer from '../TourPanel/Tour';
import useUrlSearchParams from '../hooks/useUrlSearchParams';
import { UnreadLeadsMsgSelectors, UnreadLeadsMsgOperations } from '../../../ducks/unreadLeadsMsg';

const siteId = process.env.REACT_APP_BUILD_SITE;

function PostingsList() {
  const [page, setPage] = useState(1);
  const limit = 20;
  const [loadingAction, setLoadingAction] = useState(false);
  const dispatch = useDispatch();
  const postingsData = useSelector(PostingsSelectors.getPostingsState);
  const { appliedFilters, searchCriteria, filterOnlineFirst } = useSelector(FilterSelectors.getFiltersState);
  const { userTypeId, publisherTypeId, isSeeker, isLoading, isVAPublisher, userLoggedId } = useSelector(
    userInfoSelectors.getuserInfoState
  );
  const AdminUserTypes = ['1', '2'];
  const userCanEditPermissions = AdminUserTypes.includes(userTypeId) && publisherTypeId !== '1';
  const prevFilter = useRef('');
  const device = getDevice();
  const finishSelectors = useSelector(SuspendSelectors.getSuspendState);
  const successfulFinish = finishSelectors.recievedSuccessfulResponse;
  const { isParticularNotFree } = finishSelectors;
  const errorFinish = finishSelectors.recievedErrorResponse;
  const currentFinishPosting = finishSelectors.currentPosting;
  const publishSelectors = useSelector(PublishSelectors.getPublishState);
  const reservationSelectors = useSelector(ReservationSelectors.getReservationState);
  const archiveSelectors = useSelector(ArchiveSelectors.getArchiveState);
  const particularNotFreeArchive = archiveSelectors.particularNotFree;
  const cancelReservationSelectors = useSelector(CancelReservationSelectors.getCancelReservationState);
  const {
    updatedUserResponsible,
    errorUpdatingUserResponsible,
    bulkSuccessful,
    bulkError,
    loadingUpdateUserResponsible
  } = useSelector(ResponsibleUpdateSelectors.getResponsibleUpdateState);
  const { sendUnreadLeadsMsgSuccess, sendUnreadLeadsMsgError } = useSelector(
    UnreadLeadsMsgSelectors.getUnreadLeadsMsgState
  );
  const successfulPublish = publishSelectors.recievedSuccessfulResponse;
  const errorPublish = publishSelectors.recievedErrorResponse;
  const currentPublishPosting = publishSelectors.currentPosting;
  const { resetSuspendPosting } = SuspendOperations;
  const { resetPublishPosting } = PublishOperations;
  const { resetUnreadLeads } = UnreadLeadsMsgOperations;
  const [finishedAction, setFinishedAction] = useState({
    postingId: '',
    action: '',
    result: '',
    isParticularNotFree: false
  });
  const fetchCredits = creditsOperations.fetch;
  const { t } = useTranslation();
  const { availableFacets } = useUrlSearchParams();
  const isDesktopSmall = device === 'desktop' && window.matchMedia('(max-width: 1281px)').matches;
  const callDuplicatedPosting = siteId !== 'VAMX';
  const actionFromPublisherCookie = Cookies.get('snackbarMessage');
  const actionFromNewPublisherCookie = Cookies.get('postingSavedMessage');
  const { mainReputationLevel } = useSelector(ScoreSelectors.getReputationState);

  const snackBarTextBuilder = (action, result, particularNotFree, bulkValue, offline) => {
    if (action === 'finish' && result === 'SUCCESS' && (publisherTypeId !== '1' || particularNotFree)) {
      return (
        <CustomComponent>
          <p>
            {t(`panel.avisos.finish.snackbar.SUCCESS.particularNotFree${bulkValue > 1 ? '.plural' : ''}`, {
              value: bulkValue
            })}
          </p>
          <a href='/contrataciones/resumenCuenta.bum'>
            {t('panel.avisos.finish.snackbar.SUCCESS.particularNotFree.contrataciones')}
          </a>
        </CustomComponent>
      );
    }
    if (action === 'archive' && result === 'SUCCESS' && particularNotFree) {
      return (
        <CustomComponent>
          <p>
            {t(
              `panel.avisos.archive.snackbar.SUCCESS${!offline ? '.particularNotFree' : ''}${
                bulkValue > 1 ? '.plural' : ''
              }`,
              {
                value: bulkValue
              }
            )}
          </p>
          {!offline && (
            <a href='/contrataciones/resumenCuenta.bum'>
              {t('panel.avisos.archive.snackbar.SUCCESS.particularNotFree.contrataciones')}
            </a>
          )}
        </CustomComponent>
      );
    }
    if (action === 'updateResponsibleBulk') {
      return t(`panel.updateResponsible.${action}.snackbar.${result}${bulkValue > 1 ? '.plural' : ''}`, {
        value: bulkValue
      });
    }

    if (bulkValue && bulkValue > 1) {
      return t(`panel.avisos.${action}.snackbar.${result}.plural`, {
        value: bulkValue
      });
    }

    if (action === 'unreadLeadsMsg') {
      return t(`panel.avisos.${action}.snackbar.${result}`);
    }

    if (action === 'downloadPostingsQuality') {
      if (result === 'SUCCESS') {
        return t('panel.quality.generalData.downloadingData.success');
      }
      if (result === 'ERROR') {
        return t('panel.quality.generalData.downloadingData.error');
      }
    }

    return t(`panel.avisos.${action}.snackbar.${result}`);
  };

  useEffect(() => {
    if (publisherTypeId) {
      dispatch(PostingsOperations.getFinishReason(publisherTypeId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publisherTypeId]);

  useEffect(() => {
    if (callDuplicatedPosting) {
      dispatch(PostingsOperations.getDuplicatePostings());
    }
    dispatch(PostingsOperations.checkOutdatedCompany());
    execCookiesDL();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isLoading && !isSeeker) {
      dispatch(fetchCredits());
      dispatch(creditsOperations.getPublicationPlansByOperationType(2, 2));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const onlySortApplied =
    (searchCriteria && searchCriteria.split(':').length === 2 && searchCriteria.split(':')[0] === 'sort') ||
    (searchCriteria &&
      searchCriteria.split(';').length === 2 &&
      searchCriteria.split(';')[0].split(':')[0] === 'sort' &&
      searchCriteria.split(';')[1].split(':')[0] === 'onlineFirst');

  const getPostings = withoutLoading => {
    const queryParams = {
      page,
      limit: 20
    };
    if (searchCriteria) {
      queryParams.searchParameters = searchCriteria;
    }
    // Pido los postings actualizados en un timeout para darle tiempo de procesar a la base.
    setTimeout(() => {
      dispatch(PostingsOperations.fetch(queryParams, true, false, withoutLoading));
      dispatch(fetchCredits());
      prevFilter.current = appliedFilters;
    }, 2000);
  };

  const refreshOnFailedRequests = () => {
    // se esperan 5 segundos para darle tiempo a procesar al pdj
    setTimeout(() => {
      getPostings(true);
    }, 5000);
  };

  const snackbarActionParser = action => {
    const actionsTypes = {
      SUSPEND: 'finish',
      PUBLISH: 'publish'
    };
    const currentAction = actionsTypes[action];
    return currentAction || action;
  };

  // EFECTO FINALIZAR O PUBLICAR POSTING////////////////////////////////////////////////////////////
  useEffect(() => {
    if (successfulFinish || successfulPublish) {
      const queryParams = {
        page,
        limit: 20
      };
      if (searchCriteria) {
        queryParams.searchParameters = searchCriteria;
      }
      if (finishSelectors.bulkSuccessful || publishSelectors.bulkSuccessful) {
        getPostings();
      }

      // SETEO LA INFO PARA QUE EL POSTING MUESTRE EL SNACKBAR DE ÉXITO O ERROR
      if (finishSelectors.bulkSuccessful || finishSelectors.bulkError) {
        // SI SE TRATA DEL RESULTADO DE FINALIZACIÓN...
        if (finishSelectors.bulkSuccessful) {
          setFinishedAction({
            postingId: '',
            action: 'SUSPEND',
            result: 'SUCCESS',
            component: snackBarTextBuilder(
              snackbarActionParser('SUSPEND'),
              'SUCCESS',
              isParticularNotFree,
              finishSelectors.bulkSuccessful
            ),
            keepOpen: false
          });
        }
        if (finishSelectors.bulkError) {
          refreshOnFailedRequests();
          setFinishedAction({
            postingId: '',
            action: 'SUSPEND',
            result: 'ERROR',
            component: snackBarTextBuilder(snackbarActionParser('SUSPEND'), 'ERROR', false, finishSelectors.bulkError),
            keepOpen: false
          });
        }
      } else if (publishSelectors.bulkSuccessful || publishSelectors.bulkError) {
        // SI SE TRATA DEL RESULTADO DE PUBLICACIÓN...
        if (publishSelectors.bulkSuccessful) {
          setFinishedAction({
            postingId: '',
            action: 'PUBLISH',
            result: 'SUCCESS',
            component: snackBarTextBuilder(
              snackbarActionParser('PUBLISH'),
              'SUCCESS',
              false,
              publishSelectors.bulkSuccessful
            ),
            keepOpen: false
          });
        }
        if (publishSelectors.bulkError) {
          refreshOnFailedRequests();
          setFinishedAction({
            postingId: '',
            action: 'PUBLISH',
            result: 'ERROR',
            component: snackBarTextBuilder(snackbarActionParser('PUBLISH'), 'ERROR', false, publishSelectors.bulkError),
            keepOpen: false
          });
        }
      }
    }
    if (errorFinish || errorPublish) {
      refreshOnFailedRequests();
      if (errorFinish) {
        setFinishedAction({
          postingId: '',
          action: 'SUSPEND',
          result: 'ERROR',
          component: snackBarTextBuilder(snackbarActionParser('SUSPEND'), 'ERROR', false, finishSelectors.bulkError),
          keepOpen: false
        });
      } else {
        setFinishedAction({
          postingId: currentFinishPosting || currentPublishPosting,
          action: 'PUBLISH',
          result: 'ERROR',
          component: snackBarTextBuilder(snackbarActionParser('PUBLISH'), 'ERROR', false, publishSelectors.bulkError)
        });
      }
      setLoadingAction(false);
    }
    // SANEAMIENTO DEL STORE CORRESPONDIENTE

    if (successfulPublish || errorPublish) {
      dispatch(resetPublishPosting());
    }

    if (successfulFinish || errorFinish) {
      dispatch(resetSuspendPosting());
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successfulFinish, errorFinish, successfulPublish, errorPublish, isParticularNotFree]);

  // EFECTO PARA ENVIO DE MENSAJE A CONSULTAS SIN RESPONDER, LUEGO DE LA FINALIZACIÓN DEL AVISO
  useEffect(() => {
    if (sendUnreadLeadsMsgSuccess) {
      // SETEO LA INFO PARA QUE EL POSTING MUESTRE EL SNACKBAR DE ÉXITO O ERROR
      setFinishedAction({
        postingId: '',
        action: 'unreadLeadsMsg',
        result: 'SUCCESS',
        component: snackBarTextBuilder('unreadLeadsMsg', 'SUCCESS')
      });
    }
    if (sendUnreadLeadsMsgError) {
      setFinishedAction({
        postingId: '',
        action: 'unreadLeadsMsg',
        result: 'ERROR',
        component: snackBarTextBuilder('unreadLeadsMsg', 'ERROR')
      });
    }
    // SANEAMIENTO DEL STORE CORRESPONDIENTE (para evitar que se siga mostrando el snackbar)
    if (sendUnreadLeadsMsgSuccess || sendUnreadLeadsMsgError) {
      dispatch(resetUnreadLeads());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendUnreadLeadsMsgSuccess, sendUnreadLeadsMsgError]);

  // EFECTO PARA RESERVADO
  useEffect(() => {
    if (reservationSelectors.recievedSuccessfulResponse) {
      getPostings();
      // SETEO LA INFO PARA QUE EL POSTING MUESTRE EL SNACKBAR DE ÉXITO O ERROR
      setFinishedAction({
        postingId: reservationSelectors.currentPosting,
        action: 'reservation',
        result: 'SUCCESS',
        component: snackBarTextBuilder('reservation', 'SUCCESS')
      });
    }
    if (reservationSelectors.recievedErrorResponse) {
      setFinishedAction({
        postingId: reservationSelectors.currentPosting,
        action: 'reservation',
        result: 'ERROR',
        component: snackBarTextBuilder('reservation', 'ERROR')
      });
      setLoadingAction(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationSelectors.recievedSuccessfulResponse, reservationSelectors.recievedErrorResponse]);

  // EFECTO PARA DESMARCAR RESERVADO
  useEffect(() => {
    if (cancelReservationSelectors.recievedSuccessfulResponse) {
      getPostings();
      // SETEO LA INFO PARA QUE EL POSTING MUESTRE EL SNACKBAR DE ÉXITO O ERROR
      setFinishedAction({
        postingId: cancelReservationSelectors.currentPosting,
        action: 'cancelReservation',
        result: 'SUCCESS',
        component: snackBarTextBuilder('cancelReservation', 'SUCCESS')
      });
    }
    if (cancelReservationSelectors.recievedErrorResponse) {
      setFinishedAction({
        postingId: cancelReservationSelectors.currentPosting,
        action: 'cancelReservation',
        result: 'ERROR',
        component: snackBarTextBuilder('cancelReservation', 'ERROR')
      });
      setLoadingAction(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelReservationSelectors.recievedSuccessfulResponse, cancelReservationSelectors.recievedErrorResponse]);

  // EFECTO PARA ARCHIVAR
  useEffect(() => {
    const textKey = archiveSelectors.isDraft ? 'discard' : 'archive';
    if (archiveSelectors.recievedSuccessfulResponse && archiveSelectors.bulkSuccessful) {
      getPostings();
      // SETEO LA INFO PARA QUE EL POSTING MUESTRE EL SNACKBAR DE ÉXITO O ERROR
      setFinishedAction({
        postingId: '',
        action: 'archive',
        result: 'SUCCESS',
        component: snackBarTextBuilder(
          textKey,
          'SUCCESS',
          particularNotFreeArchive,
          archiveSelectors.bulkSuccessful,
          archiveSelectors.isOffline
        )
      });
    }
    if (archiveSelectors.recievedErrorResponse || archiveSelectors.bulkError) {
      setFinishedAction({
        postingId: '',
        action: 'archive',
        result: 'ERROR',
        component: snackBarTextBuilder(textKey, 'ERROR', false, archiveSelectors.bulkError)
      });
      setLoadingAction(false);
      refreshOnFailedRequests();
    }

    if (archiveSelectors.recievedErrorResponse || archiveSelectors.recievedSuccessfulResponse) {
      dispatch(ArchiveOperations.resetArchivePosting());
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archiveSelectors.recievedSuccessfulResponse, archiveSelectors.recievedErrorResponse]);

  // EFECTO POR ACCIONES DE 'draft', 'edit' o 'publish' DESDE EL PUBLICADOR
  useEffect(() => {
    // Se lee una cookie (en vez de un parámetro por url) para evitar que se vuelva a disparar el snackbar al recargar la página.
    if (device !== 'mobile' && actionFromPublisherCookie) {
      setFinishedAction({
        postingId: '',
        action: actionFromPublisherCookie,
        result: 'SUCCESS',
        component: t(`panel.fromPublisher.${actionFromPublisherCookie}.snackbar.SUCCESS`),
        keepOpen: false
      });
    }
    // Se elimina posteriormente la cookie:
    Cookies.remove('snackbarMessage');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // EFECTO POR ACCIONES EXITOSAS DESDE EL NUEVO PUBLICADOR PROFESIONALES
  useEffect(() => {
    // Se lee una cookie (en vez de un parámetro por url) para evitar que se vuelva a disparar el snackbar al recargar la página.
    if (device !== 'mobile' && actionFromNewPublisherCookie) {
      setFinishedAction({
        postingId: '',
        action: 'postingSaved',
        result: 'SUCCESS',
        component: t(actionFromNewPublisherCookie)
      });
    }
    // Se eliminan posteriormente las cookies:
    Cookies.remove('postingSavedMessage');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionFromNewPublisherCookie]);

  // EFECTO CAMBIO DE RESPONSABLE BULK:
  useEffect(() => {
    if (updatedUserResponsible) {
      // Pedimos los postings actualizados luego de cambiar el responsable:
      getPostings();
      setFinishedAction({
        action: 'updateResponsibleBulk',
        result: 'SUCCESS',
        component: snackBarTextBuilder('updateResponsibleBulk', 'SUCCESS', false, bulkSuccessful),
        keepOpen: false
      });
    }
    if (errorUpdatingUserResponsible) {
      refreshOnFailedRequests();
      setFinishedAction({
        action: 'updateResponsibleBulk',
        result: 'ERROR',
        component: snackBarTextBuilder('updateResponsibleBulk', 'ERROR', false, bulkError),
        keepOpen: false
      });
      setLoadingAction(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedUserResponsible, errorUpdatingUserResponsible]);

  useEffect(() => {
    // Se realiza FETCH para traer los postings con los filtros aplicados
    const queryParams = {
      page: 1,
      limit
    };
    if (searchCriteria) {
      queryParams.searchParameters = searchCriteria;
    }
    if (filterOnlineFirst) {
      queryParams.onlineFirst = filterOnlineFirst;
    }
    // valida que tenga cargado los parametros de la url para la query
    let hasChargeFilters = true;
    const params = new URLSearchParams(window.location.search);
    const paramsIdArray = params
      .toString()
      .split('&')
      .map(item => item.split('=')[0]);
    const filteredParams = paramsIdArray.filter(id => availableFacets.includes(id));
    filteredParams.forEach(p => {
      if (queryParams.searchParameters && !queryParams.searchParameters.includes(p)) {
        hasChargeFilters = false;
      }
    });
    if (hasChargeFilters) {
      dispatch(PostingsOperations.fetch(queryParams, false, false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchCriteria, filterOnlineFirst]);

  const [showTourModal, setShowTourModal] = useState(false);
  const [showTour, setShowTour] = useState(false);

  const closeTourModal = () => {
    setShowTourModal(false);
  };

  useEffect(() => {
    if (
      publishSelectors.isLoading ||
      SuspendSelectors.isLoading ||
      archiveSelectors.isLoading ||
      reservationSelectors.isLoading ||
      cancelReservationSelectors.isLoading ||
      loadingUpdateUserResponsible
    ) {
      // FLAG PARA MOSTRAR SKELLETON EN POSTINGS MIENTRAS SE REALIZAN LAS ACCIONES, SE PASA A FALSE AL VOLVER A PEDIR LOS POSTINGS O TENER ERROR EN LA ACCIÓN
      setLoadingAction(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    publishSelectors.isLoading,
    SuspendSelectors.isLoading,
    archiveSelectors.isLoading,
    archiveSelectors.isLoading,
    reservationSelectors.isLoading,
    cancelReservationSelectors.isLoading,
    loadingUpdateUserResponsible
  ]);

  useEffect(() => {
    setLoadingAction(false);
  }, [postingsData.postings]);

  useEffect(() => {
    if (userLoggedId && mainReputationLevel) {
      sendCustomTrackingInfo({
        event: 'track_content_group',
        content_group: 'Panel',
        content_type: 'Mis Avisos',
        user_id: userLoggedId,
        user_type: getPublisherTypeNameDL(publisherTypeId),
        user_reputation: getPublisherScoreValue(mainReputationLevel)
      });
    }
  }, [userLoggedId, publisherTypeId, mainReputationLevel]);

  // Mostramos snackbar de acciones provenientes de otros modulos (ej: desde el publicador-porofesionales)
  usePostingSavedSnackbar(setFinishedAction, t);

  return (
    <>
      <TourContainer setShowTour={setShowTour} showTour={showTour} />

      <SnackBarContainer action={finishedAction} deviceType={device !== 'mobile' ? 'desktop' : 'mobile'} />
      {device !== 'mobile' ? (
        <PostingsListDesktop
          device={device}
          isDesktopSmall={isDesktopSmall}
          postingsData={postingsData}
          hasFilters={searchCriteria && !onlySortApplied}
          finishedAction={finishedAction}
          setFinishedAction={setFinishedAction}
          snackbarActionParser={snackbarActionParser}
          userCanEditPermissions={userCanEditPermissions}
          setShowTour={setShowTour}
          closeTourModal={closeTourModal}
          showTourModal={showTourModal}
          setShowTourModal={setShowTourModal}
          page={page}
          setPage={setPage}
          limit={limit}
          numberOfPostings={postingsData.postingCount}
          isLoading={postingsData.isLoading}
          loadingAction={loadingAction}
          getPostings={getPostings}
          isVAPublisher={isVAPublisher}
          snackBarTextBuilder={snackBarTextBuilder}
        />
      ) : (
        <PostingsListMobile
          postingsData={postingsData}
          hasFilters={searchCriteria && !onlySortApplied}
          finishedAction={finishedAction}
          setFinishedAction={setFinishedAction}
          snackbarActionParser={snackbarActionParser}
          userCanEditPermissions={userCanEditPermissions}
          publisherTypeId={publisherTypeId}
          loadingAction={loadingAction}
          page={page}
          setPage={setPage}
          limit={limit}
          numberOfPostings={postingsData.postingCount}
          showTourModal={showTourModal}
          getPostings={getPostings}
          isVAPublisher={isVAPublisher}
        />
      )}
    </>
  );
}

const CustomComponent = styled.div`
  p,
  a {
    display: inline;
    color: white;
    white-space: normal;
  }
`;

export { PostingsList };
